import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchRepairs, updateRepairJobSafeRpc } from '@/services/repairs';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { RepairStatus } from '@/types/models';
import { Modal } from '@/components/common/Modal';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { Pagination } from '@/components/common/Pagination';
import { SearchInput } from '@/components/common/SearchInput';
import { CreateRepairForm } from '@/features/repairs/CreateRepairForm';
import { RepairDetail } from '@/features/repairs/RepairDetail';
import { useAuth } from '@/context/AuthContext';
import {
  Wrench,
  Plus,
  ChevronDown,
  CalendarDays,
  User,
  Loader2,
  Kanban,
  Table as TableIcon,
  Clock,
} from 'lucide-react';
import { PAGE_SIZE } from '@/lib/constants';

const KANBAN_COLUMNS: Array<{ id: RepairStatus; label: string; bg: string; text: string }> = [
  { id: 'received', label: 'Received', bg: 'bg-blue-50', text: 'text-blue-800' },
  { id: 'diagnosing', label: 'Diagnosing', bg: 'bg-purple-50', text: 'text-purple-800' },
  { id: 'waiting_approval', label: 'Waiting Approval', bg: 'bg-amber-50', text: 'text-amber-800' },
  { id: 'in_progress', label: 'In Progress', bg: 'bg-indigo-50', text: 'text-indigo-800' },
  { id: 'waiting_parts', label: 'Waiting Parts', bg: 'bg-orange-50', text: 'text-orange-800' },
  { id: 'completed', label: 'Completed', bg: 'bg-green-50', text: 'text-green-800' },
  { id: 'returned', label: 'Delivered', bg: 'bg-teal-50', text: 'text-teal-800' },
  { id: 'cannot_repair', label: 'Cannot Repair', bg: 'bg-red-50', text: 'text-red-800' },
  { id: 'cancelled', label: 'Cancelled', bg: 'bg-gray-50', text: 'text-gray-800' },
];

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-PK', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatCurrency(val: number | null | undefined): string {
  if (val == null) return '—';
  return `Rs. ${val.toLocaleString('en-PK')}`;
}

export const RepairsPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const isStaff = !hasPermission('purchases');
  const queryClient = useQueryClient();

  const [viewMode, setViewMode] = useState<'kanban' | 'table'>('kanban');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [page, setPage] = useState(0);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailRepairId, setDetailRepairId] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKeys.repairs.list({ search, status: statusFilter, priority: priorityFilter, page }),
    queryFn: () => fetchRepairs({ search, status: statusFilter, priority: priorityFilter, page, isStaff }),
    placeholderData: (prev) => prev,
  });

  // Query all repairs for Kanban board without pagination constraint if in kanban mode
  const { data: allKanbanData } = useQuery({
    queryKey: queryKeys.repairs.list({ search, status: '', priority: priorityFilter, page: 0 }),
    queryFn: () => fetchRepairs({ search, status: '', priority: priorityFilter, page: 0, isStaff }),
    enabled: viewMode === 'kanban',
  });

  const repairs = data?.data ?? [];
  const kanbanRepairs = allKanbanData?.data ?? [];
  const totalCount = data?.count ?? 0;

  const handleSearch = (val: string) => {
    setSearch(val);
    setPage(0);
  };

  const handleCreateSuccess = (repairId: string) => {
    setCreateModalOpen(false);
    void refetch();
    setDetailRepairId(repairId);
  };

  // Quick Status Transition for Kanban Card
  const quickStatusMutation = useMutation({
    mutationFn: async ({ repairId, newStatus }: { repairId: string; newStatus: string }) => {
      await updateRepairJobSafeRpc({
        repair_id: repairId,
        status: newStatus,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.repairs.all });
    },
  });

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-brand-black flex items-center space-x-2">
            <Wrench className="w-5 h-5 text-brand-green" />
            <span>Repair Jobs & Service Counter</span>
          </h1>
          <p className="text-xs text-brand-muted mt-0.5">
            {isLoading ? 'Loading…' : `${totalCount.toLocaleString('en-PK')} repair job${totalCount !== 1 ? 's' : ''} tracked`}
          </p>
        </div>

        <div className="flex items-center space-x-2 shrink-0">
          {/* View Toggle */}
          <div className="flex items-center space-x-1 bg-white p-1 rounded-md border border-brand-border text-xs">
            <button
              onClick={() => setViewMode('kanban')}
              className={`flex items-center space-x-1 px-2.5 py-1 rounded font-semibold transition-colors ${
                viewMode === 'kanban' ? 'bg-brand-green text-white' : 'text-brand-muted hover:text-brand-black'
              }`}
            >
              <Kanban className="w-3.5 h-3.5" />
              <span>Kanban</span>
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`flex items-center space-x-1 px-2.5 py-1 rounded font-semibold transition-colors ${
                viewMode === 'table' ? 'bg-brand-green text-white' : 'text-brand-muted hover:text-brand-black'
              }`}
            >
              <TableIcon className="w-3.5 h-3.5" />
              <span>Table</span>
            </button>
          </div>

          <button
            onClick={() => setCreateModalOpen(true)}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-brand-green text-white text-sm font-bold rounded-md hover:bg-brand-green-hover transition-colors shadow-sm"
            id="new-repair-btn"
          >
            <Plus className="w-4 h-4" />
            <span>New Repair Job</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1">
          <SearchInput
            value={search}
            onChange={handleSearch}
            placeholder="Search job number, device, issue..."
          />
        </div>

        {viewMode === 'table' && (
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              className="h-9 pl-3 pr-8 bg-white border border-brand-border rounded-md text-xs text-brand-black focus:outline-none focus:ring-2 focus:ring-brand-green appearance-none"
            >
              <option value="">All Statuses</option>
              {KANBAN_COLUMNS.map((col) => (
                <option key={col.id} value={col.id}>
                  {col.label}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-brand-muted" />
          </div>
        )}

        <div className="relative">
          <select
            value={priorityFilter}
            onChange={(e) => {
              setPriorityFilter(e.target.value);
              setPage(0);
            }}
            className="h-9 pl-3 pr-8 bg-white border border-brand-border rounded-md text-xs text-brand-black focus:outline-none focus:ring-2 focus:ring-brand-green appearance-none"
          >
            <option value="">All Priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="normal">Normal</option>
            <option value="low">Low</option>
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-brand-muted" />
        </div>
      </div>

      {/* Error */}
      {isError && (
        <ErrorAlert message={parseDatabaseError(error)} onRetry={() => refetch()} />
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
        </div>
      )}

      {/* Kanban Board View */}
      {!isLoading && !isError && viewMode === 'kanban' && (
        <div className="flex space-x-3 overflow-x-auto pb-4 pt-1 min-h-[60vh]">
          {KANBAN_COLUMNS.map((col) => {
            const jobsInCol = kanbanRepairs.filter((r) => r.status === col.id);
            return (
              <div
                key={col.id}
                className="w-72 shrink-0 bg-brand-bg/70 border border-brand-border rounded-xl flex flex-col max-h-[75vh]"
              >
                {/* Column Header */}
                <div className={`p-3 rounded-t-xl border-b border-brand-border flex items-center justify-between font-bold text-xs ${col.bg} ${col.text}`}>
                  <span>{col.label}</span>
                  <span className="px-2 py-0.5 bg-white/80 rounded-full font-mono text-[10px]">
                    {jobsInCol.length}
                  </span>
                </div>

                {/* Column Cards */}
                <div className="p-2 space-y-2 overflow-y-auto flex-1 text-xs">
                  {jobsInCol.length === 0 ? (
                    <div className="py-8 text-center text-brand-muted text-[11px] italic">
                      No jobs in {col.label.toLowerCase()}
                    </div>
                  ) : (
                    jobsInCol.map((job) => {
                      const deviceLabel =
                        job.customer_device
                          ? `${job.customer_device.brand || ''} ${job.customer_device.model || ''}`.trim()
                          : job.inventory_laptop
                          ? `${job.inventory_laptop.brand || ''} ${job.inventory_laptop.model || ''}`.trim()
                          : 'Device';

                      return (
                        <div
                          key={job.id}
                          onClick={() => setDetailRepairId(job.id)}
                          className="p-3 bg-white border border-brand-border rounded-lg shadow-2xs hover:border-brand-green cursor-pointer space-y-2 transition-all"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-mono font-bold text-brand-black text-[11px]">
                              #{job.job_number}
                            </span>
                            <span
                              className={`px-1.5 py-0.5 text-[9px] font-bold rounded uppercase ${
                                job.priority === 'urgent'
                                  ? 'bg-red-600 text-white'
                                  : job.priority === 'high'
                                  ? 'bg-amber-600 text-white'
                                  : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {job.priority}
                            </span>
                          </div>

                          <div>
                            <p className="font-bold text-brand-black truncate">{deviceLabel}</p>
                            <p className="text-[11px] text-brand-muted truncate">
                              Cust: <strong className="text-brand-black">{job.customer?.name ?? 'Walk-in'}</strong>
                            </p>
                          </div>

                          <p className="text-[11px] text-red-800 line-clamp-2 bg-red-50/60 p-1.5 rounded">
                            {job.reported_issue}
                          </p>

                          <div className="flex items-center justify-between pt-1 border-t border-brand-border text-[10px]">
                            <span className="text-brand-muted flex items-center space-x-1">
                              <Clock className="w-3 h-3 text-brand-green" />
                              <span>{formatDate(job.received_at)}</span>
                            </span>
                            <span className="font-bold text-brand-green">
                              {formatCurrency(job.total_charge)}
                            </span>
                          </div>

                          {/* Quick Status Selector */}
                          <div className="pt-1" onClick={(e) => e.stopPropagation()}>
                            <select
                              value={job.status}
                              onChange={(e) =>
                                quickStatusMutation.mutate({ repairId: job.id, newStatus: e.target.value })
                              }
                              className="w-full text-[10px] py-1 px-1.5 bg-brand-bg border border-brand-border rounded font-semibold text-brand-black"
                            >
                              {KANBAN_COLUMNS.map((c) => (
                                <option key={c.id} value={c.id}>
                                  Move to: {c.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Table / List View */}
      {!isLoading && !isError && viewMode === 'table' && (
        <>
          {repairs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Wrench className="w-12 h-12 text-brand-muted/30 mb-3" />
              <p className="text-sm font-semibold text-brand-black">No repair jobs found</p>
              <p className="text-xs text-brand-muted mt-1">
                {search || statusFilter || priorityFilter
                  ? 'Try adjusting your search or filters.'
                  : 'Create your first repair job using the button above.'}
              </p>
            </div>
          ) : (
            <div className="bg-white border border-brand-border rounded-xl overflow-hidden shadow-xs text-xs">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-brand-bg border-b border-brand-border text-brand-muted font-semibold uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3">Received</th>
                      <th className="text-left px-4 py-3">Job #</th>
                      <th className="text-left px-4 py-3">Customer</th>
                      <th className="text-left px-4 py-3">Device / Model</th>
                      <th className="text-left px-4 py-3">Reported Issue</th>
                      <th className="text-right px-4 py-3">Total Charge</th>
                      <th className="text-left px-4 py-3">Priority</th>
                      <th className="text-left px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-border">
                    {repairs.map((job) => {
                      const deviceLabel =
                        job.customer_device
                          ? `${job.customer_device.brand || ''} ${job.customer_device.model || ''}`.trim()
                          : job.inventory_laptop
                          ? `${job.inventory_laptop.brand || ''} ${job.inventory_laptop.model || ''}`.trim()
                          : 'Device';

                      return (
                        <tr
                          key={job.id}
                          onClick={() => setDetailRepairId(job.id)}
                          className="hover:bg-brand-bg/60 cursor-pointer transition-colors"
                        >
                          <td className="px-4 py-3 text-brand-muted whitespace-nowrap">
                            <span className="flex items-center space-x-1">
                              <CalendarDays className="w-3.5 h-3.5 text-brand-green" />
                              <span>{formatDate(job.received_at)}</span>
                            </span>
                          </td>
                          <td className="px-4 py-3 font-bold text-brand-black font-mono">
                            #{job.job_number}
                          </td>
                          <td className="px-4 py-3 text-brand-muted">
                            <span className="flex items-center space-x-1 font-medium text-brand-black">
                              <User className="w-3.5 h-3.5 text-brand-muted" />
                              <span>{job.customer?.name ?? 'Walk-in Customer'}</span>
                            </span>
                          </td>
                          <td className="px-4 py-3 font-semibold text-brand-black">
                            {deviceLabel}
                          </td>
                          <td className="px-4 py-3 text-brand-muted max-w-xs truncate">
                            {job.reported_issue}
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-brand-green">
                            {formatCurrency(job.total_charge)}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`px-2 py-0.5 text-[10px] font-bold rounded uppercase ${
                                job.priority === 'urgent'
                                  ? 'bg-red-600 text-white'
                                  : job.priority === 'high'
                                  ? 'bg-amber-600 text-white'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {job.priority}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded-full uppercase ${
                                job.status === 'completed'
                                  ? 'bg-green-100 text-green-800'
                                  : job.status === 'returned'
                                  ? 'bg-blue-100 text-blue-800'
                                  : job.status === 'cancelled' || job.status === 'cannot_repair'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-amber-100 text-amber-800'
                              }`}
                            >
                              {job.status.replace('_', ' ')}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalCount > PAGE_SIZE && (
            <Pagination page={page} totalCount={totalCount} onPageChange={setPage} />
          )}
        </>
      )}

      {/* Create Repair Job Modal */}
      <Modal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title="Create New Repair Ticket"
        maxWidth="lg"
      >
        <CreateRepairForm
          onSuccess={handleCreateSuccess}
          onCancel={() => setCreateModalOpen(false)}
        />
      </Modal>

      {/* Repair Detail Modal */}
      <Modal
        isOpen={!!detailRepairId}
        onClose={() => setDetailRepairId(null)}
        title="Repair Job Ticket & Details"
        maxWidth="lg"
      >
        {detailRepairId && (
          <RepairDetail
            repairId={detailRepairId}
            onClose={() => setDetailRepairId(null)}
          />
        )}
      </Modal>
    </div>
  );
};
